<template>
  <CreatorBillingCardModal v-model:open="cardModalOpen" @saved="handleCardSaved" />

  <div v-if="showCancellationBanner" class="pointer-events-none fixed inset-x-0 top-4 z-[2300] px-4">
    <div class="pointer-events-auto mx-auto flex max-w-4xl flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 shadow-lg sm:flex-row sm:items-center sm:justify-between">
      <p class="flex items-center gap-2 text-sm text-amber-900">
        <ExclamationTriangleIcon class="h-5 w-5 shrink-0 text-amber-600" />
        <span>Your Platforma account is scheduled for cancellation on {{ formatDate(cancelEffectiveAt) }}.</span>
      </p>
      <button v-if="cancellationRestoreAllowed" type="button" class="shrink-0 text-sm font-semibold text-[#2D8CFF] underline hover:text-[#1f78df] disabled:opacity-60" :disabled="restoring" @click="restoreAccount">
        {{ restoring ? 'Restoring…' : 'Keep my account' }}
      </button>
      <span v-else class="text-xs text-amber-800">Restoration is disabled for this cancellation request.</span>
    </div>
  </div>

  <TransitionRoot as="template" :show="accountCanceled">
    <div class="fixed inset-0 z-[2250] overflow-y-auto">
      <div class="fixed inset-0 bg-zinc-950/80 backdrop-blur-md" />
      <div class="relative flex min-h-full items-center justify-center p-4 sm:p-8">
        <article class="w-full max-w-2xl rounded-3xl bg-white p-8 shadow-2xl sm:p-10">
          <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100"><ExclamationTriangleIcon class="h-7 w-7 text-zinc-600" /></div>
          <h2 class="mt-6 text-2xl font-semibold text-zinc-900">Restore your Platforma creator account</h2>
          <p class="mt-3 text-sm leading-6 text-zinc-600">This creator account was canceled. Platforma keeps it eligible for restoration for one year so its configuration and creator data can be reactivated.</p>
          <div class="mt-6 grid gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 p-5 text-sm sm:grid-cols-2"><p><span class="block text-zinc-500">Canceled</span><strong class="mt-1 block text-zinc-900">{{ formatDate(subscription?.canceledAt) }}</strong></p><p><span class="block text-zinc-500">Restore by</span><strong class="mt-1 block text-zinc-900">{{ formatDate(restoreEligibleAt) }}</strong></p></div>
          <p v-if="message" class="mt-4 rounded-xl border px-3 py-2 text-sm" :class="success ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'">{{ message }}</p>
          <p v-if="!restoreAvailable" class="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">The one-year restoration window has ended. A new creator account is required.</p>
          <button type="button" class="mt-7 w-full rounded-xl bg-[#2D8CFF] px-4 py-3 text-sm font-semibold text-white hover:bg-[#1f78df] disabled:cursor-not-allowed disabled:opacity-60" :disabled="restoring || !restoreAvailable" @click="restoreAccount">{{ restoring ? 'Restoring account…' : restoreAvailable ? 'Restore my creator account' : 'Restoration unavailable' }}</button>
          <p class="mt-3 text-center text-xs text-zinc-500">Paid plans require the current subscription payment after restoration. Free plans reactivate immediately.</p>
        </article>
      </div>
    </div>
  </TransitionRoot>

  <TransitionRoot as="template" :show="paymentBlocked">
    <div class="fixed inset-0 z-[2200] overflow-y-auto">
      <div class="fixed inset-0 bg-zinc-950/75 backdrop-blur-md" />
      <div class="relative flex min-h-full items-center justify-center p-4 sm:p-8">
        <article class="grid w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl lg:grid-cols-[1.1fr_0.9fr]">
          <div class="p-7 sm:p-10">
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50"><ExclamationTriangleIcon class="h-7 w-7 text-red-500" /></div>
            <h2 class="mt-6 text-2xl font-semibold text-zinc-900">Let’s get your Platforma account active again</h2>
            <p class="mt-3 text-sm leading-6 text-zinc-600">{{ paymentWaiting ? 'Your payment is still being confirmed. Platforma will keep checking it without submitting a duplicate charge.' : 'Your latest subscription payment did not process. Update the card if needed, then retry payment to restore creator access.' }}</p>
            <div class="mt-7 rounded-2xl border border-zinc-200 bg-zinc-50 p-5">
              <div class="flex items-center justify-between gap-4"><div><p class="font-semibold text-zinc-900">{{ tier?.name || 'Platforma creator plan' }}</p><p class="mt-1 text-sm text-zinc-500">{{ cadenceLabel }}</p></div><p class="text-xl font-semibold text-zinc-900">{{ amountText }}</p></div>
            </div>
            <p v-if="message" class="mt-4 rounded-xl border px-3 py-2 text-sm" :class="success ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'">{{ message }}</p>
          </div>
          <div class="flex flex-col justify-center bg-zinc-50 p-7 sm:p-10">
            <p class="text-sm font-semibold text-zinc-900">Payment method</p>
            <div class="mt-3 rounded-2xl bg-gradient-to-br from-zinc-800 to-zinc-950 p-5 text-white shadow-lg">
              <p class="text-xs uppercase tracking-[0.18em] text-zinc-400">Platforma billing</p>
              <p class="mt-8 text-lg tracking-[0.18em]">•••• •••• •••• {{ activeProfile?.lastFour || '----' }}</p>
              <div class="mt-5 flex justify-between text-sm"><span class="capitalize">{{ activeProfile?.brand || 'No card on file' }}</span><span>{{ cardExpiry }}</span></div>
            </div>
            <button type="button" class="mt-5 rounded-xl bg-[#2D8CFF] px-4 py-3 text-sm font-semibold text-white hover:bg-[#1f78df] disabled:cursor-not-allowed disabled:opacity-60" :disabled="retrying" @click="primaryAction">
              {{ primaryLabel }}
            </button>
            <button v-if="activeProfile?.hasPaymentMethod && !paymentWaiting" type="button" class="mt-3 rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-100" :disabled="retrying" @click="cardModalOpen = true">Use a different card</button>
            <p class="mt-4 text-center text-xs text-zinc-400">Secure payment processing is provided by {{ activeProviderLabel }}.</p>
          </div>
        </article>
      </div>
    </div>
  </TransitionRoot>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { TransitionRoot } from '@headlessui/vue';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import CreatorBillingCardModal from '@/Components/CreatorBillingCardModal.vue';

const apiBase = `${process.env.VUE_APP_API_PROXY_URL || ''}/api/platforma-creator-billing`;
const state = ref(null);
const resolved = ref(false);
const retrying = ref(false);
const restoring = ref(false);
const cardModalOpen = ref(false);
const message = ref('');
const success = ref(false);
let timer = null;

const subscription = computed(() => state.value?.subscription || null);
const tier = computed(() => subscription.value?.tier || null);
const activeProvider = computed(() => subscription.value?.preferredProcessor === 'boa' ? 'boa' : 'stripe');
const activeProviderLabel = computed(() => activeProvider.value === 'boa' ? 'Bank of America' : 'Stripe');
const activeProfile = computed(() => (state.value?.billingProfiles || []).find((profile) => profile.provider === activeProvider.value) || null);
const latestInvoice = computed(() => state.value?.latestInvoice || null);
const accountCanceled = computed(() => resolved.value && subscription.value?.status === 'canceled');
const paymentBlocked = computed(() => resolved.value && subscription.value?.status === 'past_due');
const paymentWaiting = computed(() => latestInvoice.value?.status === 'processing');
const paymentCanRetry = computed(() => latestInvoice.value?.status === 'failed');
const cancellationRestoreAllowed = computed(() => subscription.value?.metadata?.allowCancellationRestore !== false);
const showCancellationBanner = computed(() => resolved.value
  && subscription.value?.cancelAtPeriodEnd
  && subscription.value?.metadata?.showCancellationRestoreBanner !== false
  && !paymentBlocked.value);
const cancelEffectiveAt = computed(() => subscription.value?.metadata?.cancelEffectiveAt || subscription.value?.nextBillingAt);
const restoreEligibleAt = computed(() => {
  if (subscription.value?.metadata?.restoreEligibleUntil) return subscription.value.metadata.restoreEligibleUntil;
  const canceledAt = new Date(subscription.value?.canceledAt || 0);
  return Number.isNaN(canceledAt.getTime()) ? null : new Date(canceledAt.getTime() + 365 * 86400000);
});
const restoreAvailable = computed(() => cancellationRestoreAllowed.value && restoreEligibleAt.value && new Date(restoreEligibleAt.value).getTime() >= Date.now());
const amountText = computed(() => formatMoney(tier.value?.priceCents, tier.value?.currencyCode));
const cadenceLabel = computed(() => tier.value?.billingCadence === 'annual' ? 'Annual creator subscription' : 'Monthly creator subscription');
const cardExpiry = computed(() => activeProfile.value?.expiryMonth && activeProfile.value?.expiryYear ? `${String(activeProfile.value.expiryMonth).padStart(2, '0')}/${String(activeProfile.value.expiryYear).slice(-2)}` : '--/--');
const primaryLabel = computed(() => {
  if (retrying.value) return 'Processing…';
  if (!activeProfile.value?.hasPaymentMethod) return 'Add payment method';
  if (paymentCanRetry.value) return 'Process card again';
  return 'Check payment status';
});
const formatMoney = (cents, currency = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format((Number(cents) || 0) / 100);
const formatDate = (value) => { const date = value ? new Date(value) : null; return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'the end of the billing period'; };

const refresh = async () => {
  try {
    const response = await fetch(`${apiBase}/subscription`, { credentials: 'include', cache: 'no-store' });
    const payload = await response.json().catch(() => ({}));
    if (response.ok && payload?.success !== false) state.value = payload;
  } catch (_error) {
    // Keep the last verified state during a temporary network interruption.
  } finally { resolved.value = true; }
};
const retryPayment = async () => {
  if (retrying.value || !paymentCanRetry.value) return;
  retrying.value = true; message.value = ''; success.value = false;
  try {
    const response = await fetch(`${apiBase}/retry-payment`, { method: 'POST', credentials: 'include' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) throw new Error(payload?.message || 'Payment could not be completed');
    state.value = payload;
    success.value = true;
    message.value = 'Payment completed. Your creator access has been restored.';
    window.dispatchEvent(new CustomEvent('platforma:creator-subscription-updated'));
  } catch (error) { message.value = error?.message || 'Payment could not be completed'; }
  finally { retrying.value = false; await refresh(); }
};
const primaryAction = () => {
  if (!activeProfile.value?.hasPaymentMethod) cardModalOpen.value = true;
  else if (paymentCanRetry.value) void retryPayment();
  else void refresh();
};
const handleCardSaved = async () => { await refresh(); if (paymentCanRetry.value) await retryPayment(); };
const restoreAccount = async () => {
  if (restoring.value || (accountCanceled.value && !restoreAvailable.value)) return;
  restoring.value = true; message.value = ''; success.value = false;
  try {
    const response = await fetch(`${apiBase}/restore`, { method: 'POST', credentials: 'include' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) throw new Error(payload?.message || 'Unable to restore your account');
    state.value = payload;
    success.value = true;
    message.value = payload?.subscription?.status === 'past_due' ? 'Account restored. Complete the current plan payment to resume access.' : 'Your creator account has been restored.';
    window.dispatchEvent(new CustomEvent('platforma:creator-subscription-updated'));
  } catch (error) { message.value = error?.message || 'Unable to restore your account'; }
  finally { restoring.value = false; await refresh(); }
};

onMounted(() => {
  void refresh();
  timer = window.setInterval(refresh, 60000);
  window.addEventListener('platforma:creator-subscription-updated', refresh);
});
onUnmounted(() => {
  if (timer) window.clearInterval(timer);
  window.removeEventListener('platforma:creator-subscription-updated', refresh);
});
</script>
